// Step 2 — Pill-driven Diamond Verification flow.
// Mobile-first redesign:
//  • Sticky pill strip (always visible while editing)
//  • Multi-select mode → bulk-divert to CSV & bulk-edit attributes
//  • Compact single-line pill chips
//  • Bottom-sheet bulk editor
//  • Auto-scroll to pill strip after "Mark Complete & Next"

import React, { useState, useRef, useMemo } from 'react';
import { CheckCircle2, Gem, Send, CheckSquare, X as XIcon, Edit3 } from 'lucide-react';
import {
  ShapePicker, ColorPicker, ClarityPicker, SizePicker,
} from './common';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';

// ── Small read-only field cell (used in the locked pill editor) ──────────────
const LockedField = ({ label, value }) => (
  <div className="bg-white border border-emerald-200 rounded-xl px-2.5 py-1.5">
    <p className="text-[9px] font-semibold text-emerald-700 uppercase tracking-wide">{label}</p>
    <p className="text-sm font-bold text-gray-800 truncate" title={value}>{value || '—'}</p>
  </div>
);

// ── Pill chip ────────────────────────────────────────────────────────────────
const PillButton = ({ entry, active, isSelected, selectMode, onClick, onToggleSelect }) => {
  const isDiverted = !!entry.divertedToCsv;
  const isSilent = entry.isSilent || !entry.actionable;
  const isDone = entry.completed;

  const tone = isDiverted
    ? 'bg-purple-50 text-purple-700 border-purple-300 cursor-not-allowed'
    : isSilent
      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
      : isSelected
        ? 'bg-blue-50 text-blue-800 border-blue-500 ring-2 ring-blue-300'
        : active
          ? 'bg-emerald-600 text-white border-emerald-600 shadow-md scale-105'
          : isDone
            ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
            : 'bg-white text-gray-700 border-gray-300';

  const handleClick = () => {
    if (isSilent || isDiverted) return;
    if (selectMode) onToggleSelect?.();
    else onClick?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isSilent || isDiverted}
      data-testid={`pill-${entry.stoneCode || entry.serialNo || 'x'}`}
      data-selected={isSelected ? 'true' : 'false'}
      className={`shrink-0 px-3 py-2 rounded-2xl border-2 font-bold text-xs transition-all ${tone}`}
      title={isDiverted ? 'Moved to Colour Stone Verification' : isSilent ? 'Non-diamond — handled separately' : ''}
    >
      <div className="flex items-center gap-1.5 whitespace-nowrap">
        {selectMode && !isSilent && !isDiverted && (
          <span className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center ${
            isSelected ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-400'
          }`}>
            {isSelected && <CheckSquare className="w-2.5 h-2.5 text-white" />}
          </span>
        )}
        {!selectMode && isDone && !active && !isDiverted && <CheckCircle2 className="w-3.5 h-3.5" />}
        {isDiverted && <Send className="w-3.5 h-3.5" />}
        <span className="font-mono">{entry.stoneCode || `S${entry.serialNo || '?'}`}</span>
        <span className="text-[10px] font-medium opacity-80">
          · {Number(entry.weightCts || 0).toFixed(2)}ct · {entry.count || 0}pc
        </span>
        {isDiverted && <span className="text-[9px] font-bold opacity-70">→ CSV</span>}
        {!isDiverted && isSilent && <span className="text-[9px] font-bold opacity-70">SILENT</span>}
      </div>
    </button>
  );
};

// ── Editor card for the currently-active pill ────────────────────────────────
const ActivePillEditor = ({ entry, onChange, onMarkDone, onDivertToCsv }) => {
  const upd = (field, val) => onChange({ [field]: val });
  const locked = entry.isWeightLocked && !entry.weightOverridden;
  const [divertConfirm, setDivertConfirm] = useState(false);
  const [diverting, setDiverting] = useState(false);

  // Mirror of Step2PillFlow's isPillComplete — used to gate "Mark Complete".
  const isPillComplete =
    !!entry.shape &&
    (entry.colors || []).length > 0 &&
    (entry.clarities || []).length > 0 &&
    !!entry.size &&
    !!String(entry.weightCts ?? '').trim() &&
    !!String(entry.count ?? '').trim();

  const missing = [];
  if (!entry.shape) missing.push('Shape');
  if (!(entry.colors || []).length) missing.push('Colour');
  if (!(entry.clarities || []).length) missing.push('Clarity');
  if (!entry.size) missing.push('Size');
  if (!String(entry.weightCts ?? '').trim()) missing.push('Weight');
  if (!String(entry.count ?? '').trim()) missing.push('Pieces');

  const handleDivert = async () => {
    if (!onDivertToCsv) return;
    setDiverting(true);
    try {
      await onDivertToCsv(entry);
      setDivertConfirm(false);
    } finally {
      setDiverting(false);
    }
  };

  // ── Locked view (P1): pill auto-filled by ERP stone-code decoder ──────
  // Nothing here is editable; the supervisor only sees the verified summary.
  if (entry.locked) {
    const d = entry.decoded || {};
    return (
      <div
        className="bg-emerald-50/40 rounded-2xl shadow-sm border-2 border-emerald-300 p-3 mb-3"
        data-testid="active-pill-editor-locked"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] text-emerald-700 font-bold uppercase tracking-wide flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Auto-verified from ERP code
            </p>
            <p className="text-base font-bold text-gray-800 font-mono">
              {entry.stoneCode || `Stone ${entry.serialNo || '?'}`}
            </p>
          </div>
          <span className="text-[10px] bg-emerald-600 text-white px-2 py-1 rounded-full font-bold">LOCKED</span>
        </div>

        {entry.isMixSubstitution && (
          <div className="mb-3 bg-amber-50 border border-amber-300 rounded-lg p-2 text-[11px] text-amber-900 flex gap-1.5 items-start" data-testid="mix-substitution-chip">
            <span className="text-amber-600 font-bold">★</span>
            <div>
              <b>Clarity MIX → priced as VS</b> of the same colour. Admin will be notified of this auto-substitution when this DV is completed.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 text-sm">
          <LockedField label="Shape"   value={d.shape || entry.shape} />
          <LockedField label="Size"    value={(d.sizes || [entry.size]).join(' / ')} />
          <LockedField label="Colour"  value={(d.colours || entry.colors || []).join(', ')} />
          <LockedField label="Clarity" value={(entry.clarities || [d.clarity]).join(', ')} />
          <LockedField label="Weight (cts)" value={Number(entry.weightCts || 0).toFixed(3)} />
          <LockedField label="Pieces" value={String(entry.count ?? 0)} />
        </div>

        <p className="text-[10px] text-emerald-800/70 mt-3 italic">
          Decoded from stone code <span className="font-mono font-bold">{entry.stoneCode}</span>.
          All attributes are read-only — verified per the internal grading ruleset.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border-2 border-emerald-200 p-3 mb-3" data-testid="active-pill-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] text-emerald-700 font-bold uppercase tracking-wide">Editing</p>
          <p className="text-base font-bold text-gray-800 font-mono">
            {entry.stoneCode || `Stone ${entry.serialNo || '?'}`}
          </p>
        </div>
        {entry.erpSource && (
          <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-bold">
            ERP · {entry.erpSource}
          </span>
        )}
      </div>

      <ShapePicker compact selected={entry.shape} onChange={v => upd('shape', v)} />
      <ColorPicker compact selected={entry.colors} onChange={v => upd('colors', v)} label="Colour" />
      <ClarityPicker compact selected={entry.clarities} onChange={v => upd('clarities', v)} label="Clarity" />
      <SizePicker compact shape={entry.shape} selected={entry.size} onChange={v => upd('size', v)} />

      {/* Weight + Pieces */}
      <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 mb-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Weight &amp; Pieces</p>
          {entry.isWeightLocked && (
            <button
              type="button"
              onClick={() => upd('weightOverridden', !entry.weightOverridden)}
              data-testid="override-toggle"
              className={`text-[10px] font-bold px-2 py-1 rounded-full border ${
                entry.weightOverridden
                  ? 'bg-amber-100 text-amber-800 border-amber-300'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-300'
              }`}
            >
              {entry.weightOverridden ? '⚠ Manual Override' : '🔒 Locked from ERP'}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Weight (cts)</label>
            <input
              type="number" inputMode="decimal" step="0.001"
              value={entry.weightCts}
              disabled={locked}
              onChange={e => upd('weightCts', e.target.value)}
              data-testid="weight-input"
              className={`w-full px-3 py-2.5 border rounded-xl text-base focus:outline-none ${
                locked
                  ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-gray-900 border-gray-200 focus:border-emerald-500'
              }`}
            />
          </div>
          <div className="flex-1">
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Pieces</label>
            <input
              type="number" inputMode="numeric"
              value={entry.count}
              disabled={locked}
              onChange={e => upd('count', e.target.value)}
              data-testid="count-input"
              className={`w-full px-3 py-2.5 border rounded-xl text-base focus:outline-none ${
                locked
                  ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-gray-900 border-gray-200 focus:border-emerald-500'
              }`}
            />
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onMarkDone}
        disabled={!isPillComplete}
        data-testid="mark-pill-done-btn"
        className="w-full py-3 rounded-2xl bg-emerald-600 text-white font-bold text-sm active:bg-emerald-700 disabled:bg-gray-300 disabled:text-gray-500"
      >
        {isPillComplete
          ? (entry.completed ? '✓ Update & Next Stone' : 'Mark Complete & Next Stone')
          : `Pick ${missing.join(' · ')}`}
      </button>

      {/* Divert to CSV */}
      {onDivertToCsv && entry.serialNo != null && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          {!divertConfirm ? (
            <button
              type="button"
              onClick={() => setDivertConfirm(true)}
              data-testid="divert-to-csv-btn"
              className="w-full py-2.5 rounded-xl border-2 border-purple-200 text-purple-700 font-bold text-xs hover:bg-purple-50 active:bg-purple-100 flex items-center justify-center gap-1.5"
            >
              <Send className="w-3.5 h-3.5" />
              Not a diamond? Send to Colour Stone Verification
            </button>
          ) : (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
              <p className="text-[11px] font-bold text-purple-800 mb-2">
                Move this pill out of DV and into the tag&apos;s CSV record?
              </p>
              <p className="text-[10px] text-purple-700/80 mb-3">
                Use this when a pill marked as a diamond in ERP is actually a Polki or
                similar uncut stone. The Stock Supervisor will price it via the
                Gemstone Master. You can undo from the CSV side before it&apos;s classified.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDivertConfirm(false)}
                  disabled={diverting}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 font-bold text-xs"
                  data-testid="divert-cancel-btn"
                >Cancel</button>
                <button
                  type="button"
                  onClick={handleDivert}
                  disabled={diverting}
                  className="flex-1 py-2 rounded-lg bg-purple-600 text-white font-bold text-xs disabled:opacity-60"
                  data-testid="divert-confirm-btn"
                >{diverting ? 'Moving…' : 'Yes, move to CSV'}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Bottom-sheet bulk editor ─────────────────────────────────────────────────
// All 4 attributes (Shape · Colour · Clarity · Size) are mandatory. Apply stays
// disabled until every one is set. On Apply, every selected pill receives the
// full attribute set AND is marked `completed: true` if its Weight + Pieces are
// already known (ERP-locked pills satisfy this automatically). Manual pills
// without weight/count stay pending so the user can fill those next.
const BulkEditSheet = ({ open, onOpenChange, count, selectedEntries, onApply }) => {
  const [shape, setShape] = useState('');
  const [colors, setColors] = useState([]);
  const [clarities, setClarities] = useState([]);
  const [size, setSize] = useState('');

  // Reset on open/close so a previous run doesn't leak into the next.
  React.useEffect(() => {
    if (!open) {
      setShape(''); setColors([]); setClarities([]); setSize('');
    }
  }, [open]);

  // Clear Size whenever Shape changes — Size options depend on Shape.
  React.useEffect(() => { setSize(''); }, [shape]);

  const allFilled =
    !!shape && colors.length > 0 && clarities.length > 0 && !!size;

  // Will Apply auto-complete every selected pill?
  // True only when every selected pill already has Weight + Pieces (ERP-locked).
  const willMarkAllDone = allFilled && (selectedEntries || []).length > 0 &&
    (selectedEntries || []).every(e =>
      !!String(e.weightCts ?? '').trim() && !!String(e.count ?? '').trim()
    );

  const handleApply = () => {
    if (!allFilled) return;
    onApply({ shape, colors, clarities, size });
    onOpenChange(false);
  };

  const missing = [];
  if (!shape) missing.push('Shape');
  if (colors.length === 0) missing.push('Colour');
  if (clarities.length === 0) missing.push('Clarity');
  if (!size) missing.push('Size');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[88vh] overflow-y-auto rounded-t-3xl p-4 pb-6"
        data-testid="bulk-edit-sheet"
      >
        <SheetHeader className="text-left mb-3">
          <SheetTitle className="text-base font-bold text-gray-900">
            Bulk Edit · {count} stone{count === 1 ? '' : 's'}
          </SheetTitle>
          <SheetDescription className="text-xs text-gray-500">
            Pick all four attributes — they&apos;ll be applied to every selected pill,
            and each pill will be marked complete (Weight &amp; Pieces come from ERP).
          </SheetDescription>
        </SheetHeader>

        <BulkSection label="Shape" filled={!!shape}>
          <ShapePicker compact hideLabel selected={shape} onChange={setShape} />
        </BulkSection>

        <BulkSection label="Colour" filled={colors.length > 0}>
          <ColorPicker compact hideLabel selected={colors} onChange={setColors} />
        </BulkSection>

        <BulkSection label="Clarity" filled={clarities.length > 0}>
          <ClarityPicker compact hideLabel selected={clarities} onChange={setClarities} />
        </BulkSection>

        <BulkSection label="Size" filled={!!size} help={!shape ? 'Pick Shape first to see size options.' : null}>
          <SizePicker compact hideLabel shape={shape} selected={size} onChange={setSize} />
        </BulkSection>

        {!allFilled && (
          <p className="text-[11px] text-amber-700 mb-2 px-1" data-testid="bulk-missing-hint">
            Still needed: <span className="font-semibold">{missing.join(' · ')}</span>
          </p>
        )}

        <div className="flex gap-2 mt-1 sticky bottom-0 bg-white pt-3 -mx-4 px-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="bulk-edit-cancel"
            className="flex-1 py-3 rounded-2xl border-2 border-gray-200 text-gray-700 font-bold text-sm"
          >Cancel</button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!allFilled}
            data-testid="bulk-edit-apply"
            className="flex-1 py-3 rounded-2xl bg-emerald-600 text-white font-bold text-sm disabled:bg-gray-300"
          >{willMarkAllDone ? `Apply to ${count} & Mark Done` : `Apply to ${count}`}</button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const BulkSection = ({ label, filled, children, help }) => (
  <div className={`rounded-2xl border mb-3 transition-all ${
    filled ? 'border-emerald-300 bg-emerald-50/30' : 'border-gray-200 bg-gray-50/50'
  }`}>
    <div className="flex items-center justify-between px-3 py-2.5">
      <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">{label}</span>
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
        filled ? 'bg-emerald-600 text-white' : 'bg-amber-100 text-amber-700'
      }`}>{filled ? '✓' : 'Required'}</span>
    </div>
    <div className="px-3 pb-3">
      {children}
      {help && <p className="text-[11px] text-amber-700 mt-1">{help}</p>}
    </div>
  </div>
);

// ── Step 2 — Pill strip + selected-pill edit form ────────────────────────────
export const Step2PillFlow = ({
  tagNumber, erpFetching, erpError, erpTagMeta,
  entries, setEntries, activePillIdx, setActivePillIdx, fetchErpPills,
  dataSource, setDataSource, totalWeight, totalPieces,
  onDivertToCsv, onBulkDivertToCsv,
}) => {
  const updateEntry = (idx, patch) =>
    setEntries(prev => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));

  const updateEntries = (predicate, patch) =>
    setEntries(prev => prev.map((e, i) => (predicate(e, i) ? { ...e, ...patch } : e)));

  const active = activePillIdx != null ? entries[activePillIdx] : null;

  const isPillComplete = (e) =>
    !!e.shape && (e.colors || []).length > 0 && (e.clarities || []).length > 0 &&
    !!e.size && !!String(e.weightCts).trim() && !!String(e.count).trim();

  // ── Multi-select state ───────────────────────────────────────────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const pillStripRef = useRef(null);

  const selectableEntries = useMemo(
    // Locked (auto-decoded) pills are excluded from multi-select / bulk-edit /
    // bulk-divert because their attributes are fixed by the ERP code ruleset.
    () => entries.filter(e => e.actionable && !e.divertedToCsv && !e.isSilent && !e.locked),
    [entries],
  );

  const selectedEntries = useMemo(
    () => entries.filter(e => selectedIds.has(e.id)),
    [entries, selectedIds],
  );

  const enterSelectMode = () => {
    setSelectMode(true);
    setActivePillIdx(null);
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const toggleSelect = (id) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const selectAllPending = () => {
    const ids = selectableEntries.filter(e => !e.completed).map(e => e.id);
    setSelectedIds(new Set(ids));
  };

  const clearSelection = () => setSelectedIds(new Set());

  // ── Bulk actions ─────────────────────────────────────────────────────────
  // Bulk Edit now always supplies all 4 attributes (Shape/Colour/Clarity/Size).
  // Any selected pill that ALSO already has Weight + Pieces is auto-completed,
  // so it immediately drops out of the "pending" pool. Manual pills without
  // weight/count keep `completed=false` so the supervisor can fill those next.
  const applyBulkPatch = (patch) => {
    if (!Object.keys(patch).length) return;
    setEntries(prev => prev.map(e => {
      if (!selectedIds.has(e.id)) return e;
      const merged = { ...e, ...patch };
      const hasWeight = !!String(merged.weightCts ?? '').trim();
      const hasCount  = !!String(merged.count ?? '').trim();
      const isComplete =
        !!merged.shape && (merged.colors || []).length > 0 &&
        (merged.clarities || []).length > 0 && !!merged.size &&
        hasWeight && hasCount;
      return isComplete ? { ...merged, completed: true } : merged;
    }));
    // Auto-clear selection so the user immediately sees the newly-pending set.
    setSelectedIds(new Set());
  };

  const bulkMarkDone = () => {
    // Only mark pills that pass validation; warn for the rest.
    const targets = selectedEntries;
    const okIds = new Set(targets.filter(isPillComplete).map(e => e.id));
    const badCount = targets.length - okIds.size;
    if (okIds.size === 0) {
      alert('None of the selected stones have all required fields filled. Use Bulk Edit first.');
      return;
    }
    if (badCount > 0 && !window.confirm(
      `${okIds.size} of ${targets.length} selected stones are fully filled and will be marked complete.\n` +
      `${badCount} are missing data and will be skipped. Continue?`
    )) return;
    updateEntries((e) => okIds.has(e.id), { completed: true });
    setSelectedIds(prev => {
      const next = new Set(prev);
      okIds.forEach(id => next.delete(id));
      return next;
    });
  };

  const bulkDivert = async () => {
    if (!onBulkDivertToCsv) return;
    const targets = selectedEntries.filter(e => e.serialNo != null && !e.divertedToCsv);
    const skipped = selectedEntries.length - targets.length;
    if (targets.length === 0) {
      alert('Selected stones cannot be diverted (no ERP serial number, or already diverted).');
      return;
    }
    const list = targets.map(e => `• ${e.stoneCode || 'S' + e.serialNo} (${Number(e.weightCts || 0).toFixed(3)}ct · ${e.count || 0}pc)`).join('\n');
    if (!window.confirm(
      `Move ${targets.length} stone${targets.length === 1 ? '' : 's'} to Colour Stone Verification?\n\n${list}` +
      (skipped > 0 ? `\n\n${skipped} selected pill${skipped === 1 ? '' : 's'} cannot be diverted and will be skipped.` : '')
    )) return;

    setBulkBusy(true);
    try {
      await onBulkDivertToCsv(targets);
      // Clear only the ones that successfully diverted (Wizard updates state).
      setSelectedIds(prev => {
        const next = new Set(prev);
        targets.forEach(e => next.delete(e.id));
        return next;
      });
    } finally {
      setBulkBusy(false);
    }
  };

  // ── Mark complete & advance (single-pill) ────────────────────────────────
  const markCompleteAndAdvance = () => {
    if (activePillIdx == null || !active) return;
    if (!isPillComplete(active)) {
      alert('Please fill Shape, Colour, Clarity, Size, Weight & Pieces before continuing.');
      return;
    }
    updateEntry(activePillIdx, { completed: true });
    const next = entries.findIndex((e, i) =>
      i > activePillIdx && e.actionable && !e.completed && !e.divertedToCsv && i !== activePillIdx
    );
    if (next >= 0) {
      setActivePillIdx(next);
    } else {
      const earlier = entries.findIndex((e, i) =>
        i < activePillIdx && e.actionable && !e.completed && !e.divertedToCsv
      );
      setActivePillIdx(earlier >= 0 ? earlier : null);
    }
    // Snap the pill strip back into view so user lands on the new active pill.
    setTimeout(() => {
      pillStripRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const actionableTotal = entries.filter(e => e.actionable && !e.divertedToCsv).length;
  const actionableDone  = entries.filter(e =>
    e.actionable && !e.divertedToCsv && e.completed && isPillComplete(e)
  ).length;
  const allDone = actionableTotal > 0 && actionableDone === actionableTotal;

  const addManualEntry = () => {
    const newEntry = {
      id: `manual-${Date.now()}`,
      shape: '', colors: [], clarities: [], weightCts: '', count: '', size: '',
      stoneCode: '', serialNo: null, isWeightLocked: false, weightOverridden: false,
      erpSource: null, actionable: true, isSilent: false, isDiamond: true, completed: false,
    };
    setEntries(prev => [...prev, newEntry]);
    setActivePillIdx(entries.length);
  };

  const selCount = selectedIds.size;

  return (
    <>
      {/* Compact Data Source row (collapsible) */}
      <CompactDataSourceRow
        tagNumber={tagNumber}
        erpFetching={erpFetching}
        dataSource={dataSource}
        setDataSource={setDataSource}
        fetchErpPills={fetchErpPills}
      />

      {/* ERP fetch status + one-liner tag meta */}
      {erpFetching && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-2.5 mb-3 flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-indigo-700 font-semibold">Fetching from ERP for tag {tagNumber}…</p>
        </div>
      )}
      {erpError && !erpFetching && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-2.5 mb-3" data-testid="erp-error-banner">
          <p className="text-xs text-amber-800 font-semibold">{erpError}</p>
          <button
            type="button"
            onClick={addManualEntry}
            data-testid="add-manual-entry-btn"
            className="mt-1 text-xs font-bold text-amber-900 underline"
          >+ Add Entry Manually</button>
        </div>
      )}
      {erpTagMeta && !erpFetching && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2 mb-3 flex items-center justify-between gap-2" data-testid="erp-tag-banner">
          <p className="text-[11px] text-indigo-700 truncate">
            <span className="font-bold text-indigo-900">Tag {erpTagMeta.tag_id}</span>
            <span className="opacity-70"> · {Number(erpTagMeta.gross_wt || 0).toFixed(2)}g</span>
            <span className="opacity-70"> · {Number(erpTagMeta.stone_wt || 0).toFixed(2)}g stone</span>
            <span className="opacity-70"> · {erpTagMeta.stone_pcs || 0}pc</span>
          </p>
          <span className="text-[9px] text-indigo-600 font-mono uppercase shrink-0">{erpTagMeta.source}</span>
        </div>
      )}

      {/* Sticky pill strip — stays glued to the top while user scrolls into the editor */}
      {entries.length > 0 && (
        <div
          ref={pillStripRef}
          className="sticky top-0 z-20 -mx-4 px-4 py-2 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/80 mb-3"
        >
          <div className="bg-white rounded-2xl border border-gray-100 p-3 shadow-sm">
            <div className="flex items-center justify-between mb-2 gap-2">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide shrink-0">
                Stones ({actionableDone}/{actionableTotal})
              </p>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {!selectMode ? (
                  <>
                    <button
                      type="button"
                      onClick={addManualEntry}
                      data-testid="add-pill-btn"
                      className="text-[11px] font-bold text-emerald-700 active:text-emerald-900"
                    >+ Add</button>
                    {selectableEntries.length > 1 && (
                      <button
                        type="button"
                        onClick={enterSelectMode}
                        data-testid="enter-select-mode-btn"
                        className="text-[11px] font-bold text-blue-700 active:text-blue-900 flex items-center gap-1"
                      >
                        <CheckSquare className="w-3.5 h-3.5" />
                        Select
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={selectAllPending}
                      data-testid="select-all-pending-btn"
                      className="text-[11px] font-bold text-blue-700 active:text-blue-900"
                    >Select pending</button>
                    <button
                      type="button"
                      onClick={clearSelection}
                      disabled={selCount === 0}
                      data-testid="clear-selection-btn"
                      className="text-[11px] font-bold text-gray-600 active:text-gray-800 disabled:text-gray-300"
                    >Clear</button>
                    <button
                      type="button"
                      onClick={exitSelectMode}
                      data-testid="exit-select-mode-btn"
                      className="text-[11px] font-bold text-gray-700 active:text-gray-900 flex items-center gap-0.5"
                    >
                      <XIcon className="w-3 h-3" /> Done
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {entries.map((e, idx) => (
                <PillButton
                  key={e.id}
                  entry={e}
                  active={!selectMode && idx === activePillIdx}
                  isSelected={selectMode && selectedIds.has(e.id)}
                  selectMode={selectMode}
                  onClick={() => setActivePillIdx(idx)}
                  onToggleSelect={() => toggleSelect(e.id)}
                />
              ))}
            </div>
            {entries.some(e => e.isSilent) && !selectMode && (
              <p className="text-[10px] text-gray-400 mt-1 italic">
                &quot;Silent&quot; pills are non-diamond stones — handled in a separate form later.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {entries.length === 0 && !erpFetching && (
        <div className="text-center py-10 text-gray-400">
          <Gem className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No stones loaded yet</p>
          {tagNumber ? (
            <button
              type="button"
              onClick={() => fetchErpPills(tagNumber)}
              data-testid="fetch-erp-first-btn"
              className="mt-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold"
            >Fetch from ERP</button>
          ) : (
            <p className="text-xs">Enter a Tag Number on Step 1, or add manually.</p>
          )}
          <button
            type="button"
            onClick={addManualEntry}
            data-testid="add-manual-entry-empty-btn"
            className="mt-2 ml-2 px-4 py-2 rounded-xl border-2 border-gray-200 text-gray-700 text-xs font-bold"
          >+ Manual Entry</button>
        </div>
      )}

      {/* Selected-pill edit form (hidden in multi-select mode) */}
      {!selectMode && active && (
        <ActivePillEditor
          entry={active}
          onChange={patch => updateEntry(activePillIdx, patch)}
          onMarkDone={markCompleteAndAdvance}
          onDivertToCsv={onDivertToCsv}
        />
      )}

      {!selectMode && !active && entries.length > 0 && allDone && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 mb-3" data-testid="all-done-summary">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <div>
              <p className="text-sm font-bold text-emerald-800">All actionable stones completed</p>
              <p className="text-[11px] text-emerald-700/80">Review the decoded attributes below — tap Save &amp; Next to continue.</p>
            </div>
          </div>
          {/* Summary of every actionable pill's decoded attributes */}
          <div className="space-y-1.5">
            {entries
              .filter(e => e.actionable && !e.divertedToCsv && !e.isSilent)
              .map(e => {
                const colours = (e.colors || []).join(', ');
                const clarities = (e.clarities || []).join(', ');
                const sizes = e.sizesLocked && e.sizesLocked.length > 0
                  ? e.sizesLocked.join(' / ')
                  : (e.size || '');
                return (
                  <div
                    key={e.id}
                    data-testid={`pill-summary-${e.stoneCode || e.serialNo || 'x'}`}
                    className="bg-white border border-emerald-200 rounded-xl px-2.5 py-2 flex items-center gap-2 text-[11px]"
                  >
                    <span className="font-mono font-bold text-emerald-700 shrink-0">{e.stoneCode || `S${e.serialNo || '?'}`}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-700">{e.shape || '—'}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-700"><span className="text-gray-400">C:</span> {colours || '—'}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-700"><span className="text-gray-400">Cl:</span> {clarities || '—'}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-700"><span className="text-gray-400">Sz:</span> {sizes || '—'}</span>
                    <span className="ml-auto text-gray-500 font-mono shrink-0">{Number(e.weightCts || 0).toFixed(2)}ct · {e.count || 0}pc</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {!selectMode && !active && entries.length > 0 && !allDone && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-3 text-center">
          <p className="text-sm font-bold text-amber-800">Tap a stone to verify it</p>
          <p className="text-xs text-amber-700 mt-1">
            {actionableTotal - actionableDone} of {actionableTotal} actionable stones still need data.
          </p>
        </div>
      )}

      {/* Multi-select empty state */}
      {selectMode && selCount === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-3 text-center">
          <p className="text-sm font-bold text-blue-900">Tap pills to select them</p>
          <p className="text-xs text-blue-700 mt-1">
            Then bulk-assign attributes or send several to CSV at once.
          </p>
        </div>
      )}

      {/* Totals */}
      {entries.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 mb-3">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Totals (diamonds only)</p>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="bg-white rounded-xl py-2.5 shadow-sm" data-testid="total-weight">
              <p className="text-xl font-bold text-emerald-700">{totalWeight.toFixed(2)}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Total Weight (cts)</p>
            </div>
            <div className="bg-white rounded-xl py-2.5 shadow-sm" data-testid="total-pieces">
              <p className="text-xl font-bold text-emerald-700">{totalPieces}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Total Pieces</p>
            </div>
          </div>
        </div>
      )}

      {/* Floating bulk-action bar (above the sticky wizard footer) */}
      {selectMode && selCount > 0 && (
        <div
          className="fixed left-0 right-0 z-40 px-3"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)' }}
          data-testid="bulk-action-bar"
        >
          <div className="bg-gray-900 text-white rounded-2xl shadow-2xl px-3 py-2.5 flex items-center gap-2 mx-auto max-w-md">
            <span className="text-xs font-bold shrink-0" data-testid="bulk-selected-count">{selCount} selected</span>
            <div className="flex-1 flex gap-1.5 justify-end flex-wrap">
              <button
                type="button"
                onClick={() => setBulkEditOpen(true)}
                data-testid="bulk-edit-btn"
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-emerald-600 active:bg-emerald-700 text-[11px] font-bold"
              >
                <Edit3 className="w-3.5 h-3.5" /> Bulk Edit
              </button>
              <button
                type="button"
                onClick={bulkMarkDone}
                data-testid="bulk-mark-done-btn"
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-blue-600 active:bg-blue-700 text-[11px] font-bold"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Mark Done
              </button>
              {onBulkDivertToCsv && (
                <button
                  type="button"
                  onClick={bulkDivert}
                  disabled={bulkBusy}
                  data-testid="bulk-divert-btn"
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-purple-600 active:bg-purple-700 disabled:opacity-60 text-[11px] font-bold"
                >
                  <Send className="w-3.5 h-3.5" /> {bulkBusy ? 'Sending…' : 'To CSV'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <BulkEditSheet
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        count={selCount}
        selectedEntries={selectedEntries}
        onApply={applyBulkPatch}
      />

      {/* Hidden flag for Save & Next gating (read in Wizard) */}
      <input type="hidden" data-testid="all-pills-done" value={String(allDone)} />
    </>
  );
};

// ── Compact, collapsible Data Source row ─────────────────────────────────────
const CompactDataSourceRow = ({ tagNumber, erpFetching, dataSource, setDataSource, fetchErpPills }) => {
  const [expanded, setExpanded] = useState(false);
  const labelByKey = { manual: 'Manual', erp: 'ERP', lab_certificate: 'Lab Cert' };

  return (
    <div className="bg-white rounded-xl border border-gray-100 px-3 py-2 mb-3 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          data-testid="data-source-toggle"
          className="flex items-center gap-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide"
        >
          <span>Source:</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
            dataSource === 'erp' ? 'bg-indigo-100 text-indigo-700' :
            dataSource === 'lab_certificate' ? 'bg-amber-100 text-amber-700' :
            'bg-gray-200 text-gray-700'
          }`}>{labelByKey[dataSource] || 'Manual'}</span>
        </button>
        {tagNumber && (
          <button
            type="button"
            onClick={() => fetchErpPills(tagNumber)}
            disabled={erpFetching}
            data-testid="fetch-erp-btn"
            className="text-[11px] font-bold text-indigo-600 active:text-indigo-800 disabled:text-gray-300"
          >
            {erpFetching ? 'Fetching…' : 'Re-fetch ERP'}
          </button>
        )}
      </div>
      {expanded && (
        <div className="grid grid-cols-3 gap-2 mt-2">
          {[
            { key: 'manual',          label: 'Manual',    tone: 'bg-gray-700' },
            { key: 'erp',             label: 'ERP',       tone: 'bg-indigo-600' },
            { key: 'lab_certificate', label: 'Lab Cert',  tone: 'bg-amber-600' },
          ].map(opt => {
            const isActive = dataSource === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => { setDataSource(opt.key); setExpanded(false); }}
                data-testid={`data-source-${opt.key}`}
                className={`py-2 rounded-xl text-[11px] font-bold border-2 transition-all ${
                  isActive
                    ? `${opt.tone} text-white border-transparent shadow-sm`
                    : 'bg-white text-gray-500 border-gray-200 active:bg-gray-50'
                }`}
              >{opt.label}</button>
            );
          })}
        </div>
      )}
    </div>
  );
};
